import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';

import { RouteFilters } from './route-filters/route-filters';
import { RouteTable, LoadState } from './route-table/route-table';
import { RouteFiltersApi, RouteItem } from '../../../../pages/admin-dashboard/admin-route-management/routes.model';
import { RoutesService } from '../../../../services/route/route-service';

@Component({
  selector: 'app-routes-list-component',
  imports: [RouteFilters, RouteTable],
  templateUrl: './routes-list-component.html',
  styleUrl: './routes-list-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoutesListComponent implements OnInit {
  private routesService = inject(RoutesService);
  private router = inject(Router);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  state = signal<LoadState>('loading');
  routes = signal<RouteItem[] | null>(null);
  currentPage = signal<number>(1);
  itemsPerPage = signal<number>(25);
  totalItems = signal<number>(0);
  isFiltersApplied = signal<boolean>(false);

  filters = signal<RouteFiltersApi>({
    orderBy: 'LAST_MODIFICATION_DATE',
    orderDir: 'DESC',
  });

  ngOnInit(): void {
    this.loadRoutes();
  }

  loadRoutes() {
    this.filters.update((f) => ({
      ...f,
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
    }));
    this.state.set('loading');

    const subscription = this.routesService.getAllRoutes(this.filters()).subscribe({
      next: (res) => {
        this.routes.set(res.list);
        this.totalItems.set(res.total);
        this.state.set('success');
      },
      error: () => this.state.set('error'),
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  get totalPages(): number {
    return Math.ceil(this.totalItems() / this.itemsPerPage());
  }

  toggleSortOrder() {
    this.filters.update((f) => ({
      ...f,
      orderDir: f.orderDir === 'ASC' ? 'DESC' : 'ASC',
    }));
    this.loadRoutes();
  }

  applyFilters(applied: RouteFiltersApi) {
    this.currentPage.set(1);
    this.isFiltersApplied.set(true);
    this.filters.update((f) => ({ ...f, ...applied, pageNum: 0, pageSize: this.itemsPerPage() }));
    this.loadRoutes();
  }

  resetFilters() {
    this.filters.set({ orderBy: 'LAST_MODIFICATION_DATE', orderDir: 'DESC' });
    this.isFiltersApplied.set(false);
    this.currentPage.set(1);
    this.loadRoutes();
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
      this.loadRoutes();
    }
  }

  onPageSizeChange(size: number) {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.loadRoutes();
  }

  navigateToDetails(id: number) {
    this.router.navigate(['/admin/routes/view-route', id]);
  }

  navigateToEdit(id: number) {
    this.router.navigate(['/admin/routes/edit', id]);
  }

  onDeleteRoute(id: number) {
    console.log('delete route', id);
  }

  onToggleActive(e: { id: number; isActive: boolean }) {
    console.log('toggle active', e);
  }

  isArabic() {
    return this.locale.startsWith('ar');
  }

  @HostListener('document:click')
  onDocumentClick() {}
}
